import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  alpha,
  Box,
  Stack,
  Typography,
} from "@mui/material";
import { Flex } from "../Flex";
import { Link as RouterLink } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ExceptionGroup } from "~/pages/findings/Exceptions/types";
import { ExceptionTypeIcon } from "~/pages/findings/Exceptions/components/ExceptionTypeIcon";
import { pluralize } from "~/lib/pluralize";
import { Text, TextCaption } from "~/components/ui-library";
import { FormatDate } from "~/lib/date";
import {
  AuthorType,
  ExceptionGroupExtension,
  ExceptionReviewAction,
  ExceptionStatus,
  GetAffectedAssetsDocument,
  GetAggregateScoresDocument,
  GetComplianceControlDocument,
  ListExceptionGroupsDocument,
  ListSpaceExceptionsDocument,
  useApplyExceptionExtensionReviewMutation,
  useApplyExceptionReviewMutation,
} from "~/operations/types";
import { RemoveCircleOutline as RemoveCircleOutlineIcon } from "@mui/icons-material";
import { useScope } from "~/hooks/useScope";
import { ExceptionGroupTables } from "~/pages/findings/Exceptions/components/ExceptionGroupTables";
import ExceptionButtons from "~/pages/findings/Exceptions/components/ExceptionButtons";
import { ExceptionGroupRowProps } from "~/pages/findings/Exceptions/components/ExceptionsGroupRow";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { RemoveExceptionsGroupDialogPayload } from "../exceptions/RemoveExceptionsGroupDialog";
import { useExceptions } from "~/components/exceptions/use-exceptions";

/**
 * TODO: we can consider only pass queries name(refetchQueries) to this component
 * instead of actual refetch functions(refetch). So we can get rid of "refetch"
 **/
interface ExceptionBarProps {
  exceptionGroup: ExceptionGroup;
  refetch?: () => void;
  extensions: ExceptionGroupExtension[];
}

export function ExceptionBar({
  exceptionGroup,
  refetch,
  extensions,
}: ExceptionBarProps) {
  const { spaceScope } = useScope();
  const [loadingAction, setLoadingAction] = useState<
    ExceptionReviewAction | undefined
  >();
  const { exceptions } = exceptionGroup;

  const [applyExceptionReview, { loading: isReviewLoading }] =
    useApplyExceptionReviewMutation();
  const [applyExceptionExtensionReview, { loading: isExtensionReviewLoading }] =
    useApplyExceptionExtensionReviewMutation();

  const { handleRemoveExceptionGroups } = useExceptions({
    scopeMrns: [exceptionGroup?.scopeMrn ?? ""],
    refetchQueries: [ListExceptionGroupsDocument, GetAggregateScoresDocument],
  });

  const removeExceptionGroupPayload: RemoveExceptionsGroupDialogPayload = {
    onRemove: handleRemoveExceptionGroups,
    exceptionMrns: exceptionGroup ? [exceptionGroup.mrn] : [],
  };

  const handleReviewActionChange: ExceptionGroupRowProps["onReviewActionChange"] =
    async (exceptionGroup, action) => {
      setLoadingAction(action);
      const isPendingExtension =
        exceptionGroup.status === ExceptionStatus.NeedsExtension;

      try {
        const scopeMrn = exceptionGroup.scopeMrn;
        const exceptionId = exceptionGroup.id;

        if (isPendingExtension) {
          await applyExceptionExtensionReview({
            variables: {
              input: {
                spaceMrn: spaceScope?.mrn ?? "",
                exceptionId: exceptionGroup.exceptionId,
                action,
              },
            },
            refetchQueries: [
              ListSpaceExceptionsDocument,
              GetAggregateScoresDocument,
              GetAffectedAssetsDocument,
              ListExceptionGroupsDocument,
              GetComplianceControlDocument,
            ],
            awaitRefetchQueries: true,
            notifyOnNetworkStatusChange: true,
            onCompleted: () => {
              refetch?.();
              enqueueSnackbar(
                "Exception extension review applied successfully",
                {
                  variant: "success",
                },
              );
            },
          });
        } else {
          await applyExceptionReview({
            variables: { input: { scopeMrn, exceptionId, action } },
            refetchQueries: [
              ListSpaceExceptionsDocument,
              GetAggregateScoresDocument,
              GetAffectedAssetsDocument,
              ListExceptionGroupsDocument,
              GetComplianceControlDocument,
            ],
            awaitRefetchQueries: true,
            notifyOnNetworkStatusChange: true,
            onCompleted: () => {
              refetch?.();
              enqueueSnackbar("Exception review applied successfully", {
                variant: "success",
              });
            },
          });
        }
      } catch (error) {
        enqueueSnackbar("Failed to apply exception review", {
          variant: "error",
        });
      } finally {
        setLoadingAction(undefined);
      }
    };

  const exceptionCount = exceptions.length;
  const exceptionHeadline = `${exceptionCount} ${pluralize("Exception", exceptionCount)} on this finding`;
  const isAssetLevel = exceptionGroup.scopeMrn.startsWith("//assets");
  const exceptionScope = isAssetLevel ? "Single asset" : "Entire space";

  return (
    <Accordion
      data-test-id="open-exceptions"
      sx={{
        borderRadius: "4px !important",
        overflow: "hidden",
        "&.Mui-expanded": {
          margin: "0",
        },
        "&:before": {
          display: "none",
        },
        "& .MuiButtonBase-root": {
          minHeight: "20px",
        },
        "& .MuiButtonBase-root.MuiAccordionSummary-root.Mui-expanded": {
          minHeight: "unset !important",
        },
        "& .MuiAccordionSummary-root": {
          backgroundColor: "background.light",
          "&:focus-visible:not(.Mui-expanded):after": {
            content: "''",
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            border: (theme) =>
              `2px inset ${alpha(theme.palette.primary.main, 0.6)}`,
            borderRadius: 1,
          },
        },
        "& .MuiAccordionSummary-content": {
          margin: "6px 0",
        },
      }}
    >
      <AccordionSummary
        sx={{
          my: 0,
        }}
        expandIcon={
          <ExpandMoreIcon sx={{ color: "primary.main", fontSize: 24 }} />
        }
      >
        <Box display="flex" alignItems="center" width="100%">
          <Stack direction="row" alignItems="center" gap={1}>
            <RemoveCircleOutlineIcon sx={{ fontSize: 16 }} />
            <Typography variant="body2" fontWeight={700}>
              {exceptionHeadline}
            </Typography>
          </Stack>
        </Box>
      </AccordionSummary>
      <AccordionDetails
        sx={{ p: 3, pt: 2, backgroundColor: "background.paper" }}
      >
        <Flex flexDirection="column" gap={1}>
          <Flex justifyContent="space-between">
            <Flex center gap={2}>
              <ExceptionTypeIcon action={exceptionGroup.action} />
              <Text bold fontSize={24}>
                {exceptionGroup.title}
              </Text>
            </Flex>
            <ExceptionButtons
              exceptionGroup={exceptionGroup}
              onReviewActionChange={handleReviewActionChange}
              isLoading={isReviewLoading || isExtensionReviewLoading}
              loadingAction={loadingAction}
              removeExceptionGroupPayload={removeExceptionGroupPayload}
              refetch={refetch}
              enabledExtension
            />
          </Flex>
          <Flex justifyContent="space-between">
            <Flex gap={2}>
              {exceptionGroup.author && (
                <Stack direction="row" gap={0.25}>
                  <TextCaption bold secondary>
                    Created by
                  </TextCaption>
                  <TextCaption
                    secondary
                    {...(exceptionGroup.author.type ===
                      AuthorType.ServiceAccount && {
                      component: RouterLink,
                      to: `/space/settings/service-accounts?spaceId=${spaceScope?.id}&queryTerms=${exceptionGroup.author.name}`,
                      sx: {
                        color: "primary.light",
                      },
                    })}
                  >
                    {exceptionGroup.author.name} on{" "}
                    {FormatDate(exceptionGroup.createdAt)}
                  </TextCaption>
                </Stack>
              )}

              {exceptionGroup.validUntil && (
                <Stack direction="row" gap={0.25}>
                  <TextCaption bold secondary>
                    Expires
                  </TextCaption>
                  <TextCaption secondary>
                    {FormatDate(exceptionGroup.validUntil)}
                  </TextCaption>
                </Stack>
              )}

              <Stack direction="row" gap={0.25}>
                <TextCaption bold secondary>
                  Scope
                </TextCaption>
                <TextCaption secondary>{exceptionScope}</TextCaption>
              </Stack>

              {exceptionGroup.status === ExceptionStatus.Approved && (
                <Stack direction="row" gap={0.25}>
                  <TextCaption bold secondary>
                    Approved by
                  </TextCaption>
                  <TextCaption secondary>
                    {exceptionGroup.reviewer?.name}
                  </TextCaption>
                </Stack>
              )}
            </Flex>
          </Flex>
          {exceptionGroup.justification && (
            <Flex sx={{ mt: 0 }}>
              <Typography
                color="text.secondary"
                data-test-id="exception-justification"
              >
                {exceptionGroup.justification}
              </Typography>
            </Flex>
          )}
          {spaceScope?.id && (
            <ExceptionGroupTables
              exceptionGroup={exceptionGroup}
              exceptionScope={isAssetLevel ? "asset" : "space"}
              extensions={extensions}
            />
          )}
        </Flex>
      </AccordionDetails>
    </Accordion>
  );
}
